import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, RotateCcw, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/api";
import { BENEFIT_ICONS, getBenefitIcon } from "../../lib/benefitIcons";
import { HistoryButton } from "./HistoryDialog";

function useContent(key) {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        apiClient
            .get(`/content/${key}`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
    }, [key]);

    const save = async () => {
        if (!data.headline?.trim()) {
            toast.error("Die Überschrift darf nicht leer sein.");
            return;
        }
        setSaving(true);
        try {
            await apiClient.put(`/admin/content/${key}`, data);
            toast.success("Gespeichert. Die Startseite ist aktualisiert.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Auf den Standardtext zurücksetzen?")) return;
        try {
            const r = await apiClient.delete(`/admin/content/${key}`);
            setData(r.data);
            toast.success("Auf Standard zurückgesetzt.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
        }
    };

    return { data, setData, saving, save, reset };
}

function EditorHeader({ title, subtitle, saving, onSave, onReset, testPrefix, historyKey, onRestore }) {
    return (
        <div className="flex items-center justify-between mb-5">
            <div>
                <h2 className="text-lg font-bold text-moebus-deep">{title}</h2>
                <p className="text-sm text-slate-500">{subtitle}</p>
            </div>
            <div className="flex items-center gap-2">
                {historyKey && <HistoryButton historyKey={historyKey} onRestore={onRestore} />}
                <button
                    data-testid={`${testPrefix}-reset`}
                    onClick={onReset}
                    className="inline-flex items-center gap-2 text-sm text-slate-600 hover:bg-slate-100 px-3 py-2 rounded-md border border-moebus-border"
                >
                    <RotateCcw size={14} /> Standard
                </button>
                <button
                    data-testid={`${testPrefix}-save`}
                    onClick={onSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 text-sm font-medium text-white bg-moebus-deep hover:bg-moebus-deep/90 px-4 py-2 rounded-md disabled:opacity-50"
                >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
                </button>
            </div>
        </div>
    );
}

function ParagraphList({ paragraphs, onChange, testPrefix }) {
    return (
        <div className="grid gap-2">
            {paragraphs.map((p, i) => (
                <div key={i} className="flex gap-2">
                    <textarea
                        data-testid={`${testPrefix}-paragraph-${i}`}
                        value={p}
                        onChange={(e) => onChange(paragraphs.map((x, j) => (j === i ? e.target.value : x)))}
                        rows={3}
                        className="flex-1 text-sm border border-moebus-border rounded-md px-3 py-2"
                    />
                    <button
                        data-testid={`${testPrefix}-paragraph-${i}-remove`}
                        onClick={() => onChange(paragraphs.filter((_, j) => j !== i))}
                        disabled={paragraphs.length <= 1}
                        className="p-2 self-start rounded-md text-red-500 hover:bg-red-50 disabled:opacity-30"
                        title="Absatz entfernen"
                    >
                        <Trash2 size={15} />
                    </button>
                </div>
            ))}
            <button
                data-testid={`${testPrefix}-paragraph-add`}
                onClick={() => onChange([...paragraphs, ""])}
                disabled={paragraphs.length >= 6}
                className="inline-flex items-center gap-1.5 self-start text-sm text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-md disabled:opacity-40"
            >
                <Plus size={14} /> Absatz hinzufügen
            </button>
        </div>
    );
}

function LightPreview({ kicker, headline, paragraphs, children }) {
    return (
        <div data-testid="content-preview" className="bg-moebus-surface border border-moebus-border rounded-xl p-5 md:p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-moebus-green font-semibold mb-3">
                Vorschau · {kicker}
            </div>
            <div className="font-heading font-bold text-xl md:text-2xl text-moebus-deep leading-tight">
                {headline || "Überschrift…"}
            </div>
            <div className="mt-3 space-y-2 text-sm text-slate-600 leading-relaxed">
                {(paragraphs || []).map((p, i) => (
                    <p key={i}>{p}</p>
                ))}
            </div>
            {children}
        </div>
    );
}

const Field = ({ label, value, onChange, testid }) => (
    <label className="grid gap-1 text-xs font-medium text-slate-500">
        {label}
        <input
            data-testid={testid}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="text-sm font-normal text-slate-800 border border-moebus-border rounded-md px-3 py-2"
        />
    </label>
);

export const AboutEditor = () => {
    const { data, setData, saving, save, reset } = useContent("about");
    if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>;
    const set = (k, v) => setData({ ...data, [k]: v });

    return (
        <div className="max-w-4xl" data-testid="about-editor">
            <EditorHeader
                title="„Wer wir sind“ bearbeiten"
                subtitle="Überschrift, Textabsätze und die zwei Info-Boxen der Startseite."
                saving={saving} onSave={save} onReset={reset} testPrefix="about"
                historyKey="content_about" onRestore={setData}
            />
            <div className="grid gap-5">
                <LightPreview kicker="Wer wir sind" headline={data.headline} paragraphs={data.paragraphs}>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                        <div className="bg-white border border-moebus-border rounded-lg p-3">
                            <div className="font-heading font-bold text-moebus-deep">
                                {data.stat1_number}
                                <span className="text-moebus-green">{data.stat1_suffix}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">{data.stat1_label}</div>
                        </div>
                        <div className="bg-white border border-moebus-border rounded-lg p-3">
                            <div className="font-heading font-bold text-moebus-deep">{data.stat2_title}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{data.stat2_label}</div>
                        </div>
                    </div>
                </LightPreview>
                <div className="bg-white border border-moebus-border rounded-xl p-5 grid gap-4">
                    <Field label="Überschrift" value={data.headline} onChange={(v) => set("headline", v)} testid="about-headline" />
                    <div>
                        <div className="text-xs font-medium text-slate-500 mb-1.5">Textabsätze</div>
                        <ParagraphList paragraphs={data.paragraphs || []} onChange={(v) => set("paragraphs", v)} testPrefix="about" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-moebus-border">
                        <div className="grid gap-3">
                            <div className="text-xs font-semibold text-moebus-deep">Info-Box 1</div>
                            <div className="grid grid-cols-2 gap-2">
                                <Field label="Zahl (animiert)" value={data.stat1_number} onChange={(v) => set("stat1_number", v)} testid="about-stat1-number" />
                                <Field label="Zusatz" value={data.stat1_suffix} onChange={(v) => set("stat1_suffix", v)} testid="about-stat1-suffix" />
                            </div>
                            <Field label="Beschreibung" value={data.stat1_label} onChange={(v) => set("stat1_label", v)} testid="about-stat1-label" />
                        </div>
                        <div className="grid gap-3 content-start">
                            <div className="text-xs font-semibold text-moebus-deep">Info-Box 2</div>
                            <Field label="Titel" value={data.stat2_title} onChange={(v) => set("stat2_title", v)} testid="about-stat2-title" />
                            <Field label="Beschreibung" value={data.stat2_label} onChange={(v) => set("stat2_label", v)} testid="about-stat2-label" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const WhatWeDoEditor = () => {
    const { data, setData, saving, save, reset } = useContent("whatwedo");
    if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>;
    const set = (k, v) => setData({ ...data, [k]: v });
    const cards = data.cards || [];
    const setCard = (i, card) => set("cards", cards.map((c, j) => (j === i ? card : c)));

    return (
        <div className="max-w-4xl" data-testid="whatwedo-editor">
            <EditorHeader
                title="„Was wir machen“ bearbeiten"
                subtitle="Überschrift, Einleitungstext und die Leistungs-Karten."
                saving={saving} onSave={save} onReset={reset} testPrefix="whatwedo"
                historyKey="content_whatwedo" onRestore={setData}
            />
            <div className="grid gap-5">
                <LightPreview kicker="Was wir machen" headline={data.headline} paragraphs={[data.intro]}>
                    <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        {cards.map((card, i) => {
                            const Icon = getBenefitIcon(card.icon);
                            return (
                                <div key={i} className="bg-white border border-moebus-border rounded-lg p-3">
                                    <div className="w-8 h-8 rounded-md bg-emerald-50 text-moebus-green flex items-center justify-center mb-2">
                                        <Icon size={15} />
                                    </div>
                                    <div className="font-heading font-semibold text-moebus-deep text-xs">{card.title || "Titel…"}</div>
                                    <div className="mt-1 text-[11px] text-slate-500 leading-snug line-clamp-2">{card.text}</div>
                                </div>
                            );
                        })}
                    </div>
                </LightPreview>
                <div className="bg-white border border-moebus-border rounded-xl p-5 grid gap-4">
                    <Field label="Überschrift" value={data.headline} onChange={(v) => set("headline", v)} testid="whatwedo-headline" />
                    <label className="grid gap-1 text-xs font-medium text-slate-500">
                        Einleitungstext
                        <textarea
                            data-testid="whatwedo-intro"
                            value={data.intro || ""}
                            onChange={(e) => set("intro", e.target.value)}
                            rows={4}
                            className="text-sm font-normal text-slate-800 border border-moebus-border rounded-md px-3 py-2"
                        />
                    </label>
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-xs font-medium text-slate-500">Leistungs-Karten</div>
                            <button
                                data-testid="whatwedo-card-add"
                                onClick={() => set("cards", [...cards, { icon: "star", title: "", text: "" }])}
                                disabled={cards.length >= 8}
                                className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-md disabled:opacity-40"
                            >
                                <Plus size={14} /> Hinzufügen
                            </button>
                        </div>
                        <div className="grid gap-3">
                            {cards.map((card, i) => (
                                <div key={i} className="flex gap-2 bg-slate-50 border border-moebus-border rounded-lg p-3">
                                    <div className="flex-1 grid gap-2">
                                        <div className="flex gap-2">
                                            <select
                                                data-testid={`whatwedo-card-${i}-icon`}
                                                value={card.icon}
                                                onChange={(e) => setCard(i, { ...card, icon: e.target.value })}
                                                className="w-44 text-sm border border-moebus-border rounded-md px-2 py-2 bg-white"
                                            >
                                                {Object.entries(BENEFIT_ICONS).map(([key, { label }]) => (
                                                    <option key={key} value={key}>{label}</option>
                                                ))}
                                            </select>
                                            <input
                                                data-testid={`whatwedo-card-${i}-title`}
                                                value={card.title}
                                                onChange={(e) => setCard(i, { ...card, title: e.target.value })}
                                                placeholder="Titel"
                                                className="flex-1 text-sm border border-moebus-border rounded-md px-3 py-2"
                                            />
                                        </div>
                                        <input
                                            data-testid={`whatwedo-card-${i}-text`}
                                            value={card.text}
                                            onChange={(e) => setCard(i, { ...card, text: e.target.value })}
                                            placeholder="Kurzbeschreibung"
                                            className="text-sm border border-moebus-border rounded-md px-3 py-2"
                                        />
                                    </div>
                                    <button
                                        data-testid={`whatwedo-card-${i}-remove`}
                                        onClick={() => set("cards", cards.filter((_, j) => j !== i))}
                                        className="p-2 self-start rounded-md text-red-500 hover:bg-red-50"
                                        title="Karte entfernen"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const JobsIntroEditor = () => {
    const { data, setData, saving, save, reset } = useContent("jobsintro");
    if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>;
    const set = (k, v) => setData({ ...data, [k]: v });

    return (
        <div className="max-w-4xl" data-testid="jobsintro-editor">
            <EditorHeader
                title="„Was wir suchen“ bearbeiten"
                subtitle="Überschrift und Einleitungstext über den Stellen-Karten. Die Karten selbst pflegst du unter „Stellen“."
                saving={saving} onSave={save} onReset={reset} testPrefix="jobsintro"
                historyKey="content_jobsintro" onRestore={setData}
            />
            <div className="grid gap-5">
                <LightPreview kicker="Was wir suchen" headline={data.headline} paragraphs={data.paragraphs} />
                <div className="bg-white border border-moebus-border rounded-xl p-5 grid gap-4">
                    <Field label="Überschrift" value={data.headline} onChange={(v) => set("headline", v)} testid="jobsintro-headline" />
                    <div>
                        <div className="text-xs font-medium text-slate-500 mb-1.5">Textabsätze</div>
                        <ParagraphList paragraphs={data.paragraphs || []} onChange={(v) => set("paragraphs", v)} testPrefix="jobsintro" />
                    </div>
                </div>
            </div>
        </div>
    );
};
